/**
 * Shared module visibility rules for SideBar dashboard modules and ModuleLauncher.
 * Access = Access Control my-access (corporate ceiling ∩ user grants) + role fallbacks
 *        + corporate subscription / plan feature locks.
 */

import { submenuAllowedByMyAccess } from './routeAccessConfig';
import {
  normalizeRole,
  isCorporateAdmin,
  hasAdminRole,
  canAccessUsers,
  canAccessStaff,
  canAccessBranches,
  canAccessVehicles,
  canAccessDesignations,
  canAccessWorksheet,
  canAccessDepartments,
  canAccessSubDepartments,
  canDo,
} from './roleAccess';

const SUBSCRIPTION_FEATURE_REQUIRED = '/subscription-feature-required';

export function canAccessMenuSubItem(subItem, ctx) {
  if (subItem?.publicAccess) return true;
  if (subItem?.section && subItem?.requiredAction && !canDo(subItem.section, subItem.requiredAction)) {
    return false;
  }
  const { menuAccessKeys, menuAccessRoutePaths, menuAccessFetchResolved, userRole } = ctx;

  if (menuAccessKeys !== null && Array.isArray(menuAccessKeys)) {
    return submenuAllowedByMyAccess(menuAccessKeys, menuAccessRoutePaths, subItem);
  }
  if (menuAccessFetchResolved && menuAccessKeys === null && subItem?.menu_key) return false;
  if (!subItem?.role) return true;

  const role = normalizeRole(subItem.role);
  const currentRole = normalizeRole(userRole || '');
  if (subItem.restricted_roles?.some((r) => currentRole === normalizeRole(r))) return false;
  if (role === 'corporate_admin') return isCorporateAdmin(userRole);
  if (role === 'admin') return hasAdminRole(userRole);
  return currentRole === role;
}

export function passesLegacyRoleRouteChecks(subItem, ctx) {
  if (ctx.menuAccessKeys !== null) return true;
  const { userRole } = ctx;
  if (subItem.to === 'settings/userlist' && !canAccessUsers(userRole)) return false;
  if (subItem.to === 'staff/staffList' && !canAccessStaff(userRole)) return false;
  if (subItem.to === 'settings/branchList' && !canAccessBranches(userRole)) return false;
  if (subItem.to === '/vehicle/vehicleList' && !canAccessVehicles(userRole)) return false;
  if (subItem.to === 'settings/designationList' && !canAccessDesignations(userRole)) return false;
  if (subItem.to === '/task/worksheet' && !canAccessWorksheet(userRole)) return false;
  if (subItem.to === 'settings/departmentList' && !canAccessDepartments(userRole)) return false;
  if (subItem.to === 'settings/subDepartmentList' && !canAccessSubDepartments(userRole)) return false;
  return true;
}

export function getVisibleMenuContent(menuItem, ctx) {
  if (!menuItem?.content) return [];
  return menuItem.content.filter(
    (subItem) => canAccessMenuSubItem(subItem, ctx) && passesLegacyRoleRouteChecks(subItem, ctx)
  );
}

/**
 * @param {object} locks - { documents, forum, invoiceScanner, project, planFeatures[] }
 */
export function isModuleSubscriptionLocked(menuItem, locks) {
  if (!menuItem) return false;
  if (menuItem.title === 'Documents') return !locks.documents;
  if (menuItem.title === 'Discussion Forum' || menuItem.menu_key === 'forum') {
    return !locks.forum;
  }
  if (menuItem.title === 'Snap Bill' || menuItem.menu_key === 'invoice_scanner') {
    return !locks.invoiceScanner;
  }
  if (menuItem.title === 'Projects' || menuItem.menu_key === 'projects') {
    return !locks.project;
  }
  return false;
}

export function isSubItemFeatureLocked(subItem, isModuleLocked, locks) {
  if (subItem?.publicAccess) return false;
  if (isModuleLocked) return true;
  if (subItem.feature_key === 'project') return !locks.project;
  if (subItem.feature_key === 'branch') return !locks.branch;
  if (subItem.feature_key === 'department') return !locks.department;
  if (subItem.feature_key === 'worksheet') return !locks.worksheet;
  if (subItem.feature_key === 'invoice_scanner') return !locks.invoiceScanner;
  if (subItem.feature_key) {
    return !(locks.planFeatures || []).includes(subItem.feature_key);
  }
  return false;
}

export function getModuleLockedRedirect(subItem, menuItem) {
  let module = 'task';
  if (subItem?.feature_key === 'document' || menuItem?.title === 'Documents') {
    module = 'document';
  } else if (
    subItem?.feature_key === 'discussion_forum' ||
    menuItem?.title === 'Discussion Forum' ||
    menuItem?.menu_key === 'forum'
  ) {
    module = 'discussion_forum';
  } else if (subItem?.feature_key === 'invoice_scanner') {
    module = 'invoice_scanner';
  } else if (
    subItem?.feature_key === 'project' ||
    menuItem?.menu_key === 'projects' ||
    menuItem?.title === 'Projects'
  ) {
    module = 'task';
  }
  return `${SUBSCRIPTION_FEATURE_REQUIRED}?module=${module}`;
}
